import React, { useState } from 'react';
import { handlePhoneInput } from '../../utils/phoneFormat';

const ConfirmGym = ({ gymData, onComplete, onBack }) => {
  const [hasIssue, setHasIssue] = useState(false);
  const [issueDescription, setIssueDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [boardStatuses, setBoardStatuses] = useState({});
  const [editedGymData, setEditedGymData] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    phone: ''
  });

  // Initialize board statuses and gym data from gymData
  React.useEffect(() => {
    if (gymData?.gym) {
      // Initialize board statuses
      if (gymData.gym.boards) {
        const initialStatuses = {};
        gymData.gym.boards.forEach(board => {
          initialStatuses[board.id] = board.status || 'active';
        });
        setBoardStatuses(initialStatuses);
      }

      // Initialize editable gym data
      setEditedGymData({
        name: gymData.gym.name || '',
        addressLine1: gymData.gym.addressLine1 || '',
        addressLine2: gymData.gym.addressLine2 || '',
        phone: gymData.gym.phone || ''
      });
    }
  }, [gymData]);

  const handleBoardStatusToggle = (boardId, currentStatus) => {
    setBoardStatuses(prev => ({
      ...prev,
      [boardId]: currentStatus === 'active' ? 'inactive' : 'active'
    }));
  };

  const handleGymDataChange = (field, value) => {
    // Apply phone formatting if it's the phone field
    const finalValue = field === 'phone' ? handlePhoneInput(value) : value;

    setEditedGymData(prev => ({
      ...prev,
      [field]: finalValue
    }));
  };

  const handleSaveEdits = () => {
    setIsEditing(false);
    // Data is already updated in state, will be passed to onComplete
  };

  const handleReportIssue = async () => {
    // In a real implementation, this would send to admin
    console.log('Issue reported:', issueDescription);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-xl p-6 sm:p-8 border-2 border-slate-900 shadow-retro">
        <div className="text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Thanks for letting us know!</h3>
          <p className="text-slate-700 mb-6">
            We've received your feedback and will reach out shortly to fix any issues.
          </p>
          <p className="text-sm text-slate-600">
            You can close this page now. We'll send you a new link once everything is corrected.
          </p>
        </div>
      </div>
    );
  }

  if (hasIssue) {
    return (
      <div className="bg-white rounded-xl p-6 sm:p-8 border-2 border-slate-900 shadow-retro">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-1 h-8 bg-gradient-to-b from-coral-500 to-magenta-500 rounded"></span>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">What's Wrong?</h2>
        </div>

        <p className="text-slate-700 mb-4">
          Please let us know what needs to be corrected and we'll fix it right away.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              What needs to be changed?
            </label>
            <textarea
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border-2 border-slate-900 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="e.g., Wrong address, missing a board, incorrect hours..."
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setHasIssue(false)}
              className="flex-1 px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all"
            >
              ← Back
            </button>
            <button
              onClick={handleReportIssue}
              disabled={!issueDescription.trim()}
              className="flex-1 px-6 py-3 bg-coral-500 hover:bg-coral-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all disabled:opacity-50"
            >
              Send to Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 sm:p-8 border-2 border-slate-900 shadow-retro">
      <div className="flex items-center gap-2 mb-6">
        <span className="w-1 h-8 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Does This Look Right?</h2>
      </div>

      <p className="text-slate-700 mb-6">
        We've set up <span className="font-bold">{gymData.gym.name}</span> based on your contract. Please confirm everything looks correct.
      </p>

      <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-xl p-6 border-2 border-slate-900 mb-6">
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2">
                <span className="w-1 h-4 bg-teal-500 rounded"></span>
                Gym Details
              </h3>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="text-sm font-semibold text-teal-600 hover:text-teal-700 underline"
              >
                {isEditing ? 'Cancel' : 'Edit'}
              </button>
            </div>
            <div className="bg-white rounded-lg p-4 border-2 border-slate-900">
              {isEditing ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Gym Name</label>
                    <input
                      type="text"
                      value={editedGymData.name}
                      onChange={(e) => handleGymDataChange('name', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Address Line 1</label>
                    <input
                      type="text"
                      value={editedGymData.addressLine1}
                      onChange={(e) => handleGymDataChange('addressLine1', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Address Line 2</label>
                    <input
                      type="text"
                      value={editedGymData.addressLine2}
                      onChange={(e) => handleGymDataChange('addressLine2', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium"
                      placeholder="Suite, Unit, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={editedGymData.phone}
                      onChange={(e) => handleGymDataChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium"
                      placeholder="(303) 555-1234"
                    />
                  </div>
                  <button
                    onClick={handleSaveEdits}
                    className="w-full px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro transition-all"
                  >
                    Save Changes
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xl font-bold text-slate-900">{editedGymData.name}</p>
                  <p className="text-slate-700 mt-1">{editedGymData.addressLine1}</p>
                  {editedGymData.addressLine2 && (
                    <p className="text-slate-700">{editedGymData.addressLine2}</p>
                  )}
                  {editedGymData.phone && (
                    <p className="text-slate-700 mt-1">
                      <span className="font-semibold">Phone:</span> {editedGymData.phone}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-2">
              <span className="w-1 h-4 bg-purple-500 rounded"></span>
              Training Boards
            </h3>
            <div className="bg-white rounded-lg p-4 border-2 border-slate-900 space-y-3">
              {gymData.gym.boards.map((board) => {
                const boardName = board.custom_name || board.board_type;
                const currentStatus = boardStatuses[board.id] || board.status;
                const isActive = currentStatus === 'active';

                return (
                  <div key={board.id} className="p-3 bg-purple-50 rounded-lg border-2 border-purple-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-900">
                          {boardName}
                        </h4>
                        <div className="text-sm text-slate-600 mt-1 space-y-1">
                          <p>
                            <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full capitalize">
                              {board.board_type}
                            </span>
                          </p>
                          {board.angle && !board.is_adjustable && (
                            <p>• Angle: {board.angle}°</p>
                          )}
                          {board.is_adjustable && (
                            <p>• Adjustable: {board.min_angle}° - {board.max_angle}°</p>
                          )}
                          {board.location_notes && (
                            <p>• Location: {board.location_notes}</p>
                          )}
                          {board.has_led && (
                            <p>• LED Integration Available</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleBoardStatusToggle(board.id, currentStatus)}
                        className={`ml-3 px-3 py-1 rounded-full border-2 font-bold text-xs transition-all ${
                          isActive
                            ? 'bg-teal-500 text-white border-teal-600 hover:bg-teal-600'
                            : 'bg-slate-300 text-slate-700 border-slate-400 hover:bg-slate-400'
                        }`}
                      >
                        {isActive ? 'Active' : 'Inactive'}
                      </button>
                    </div>
                  </div>
                );
              })}
              <p className="text-xs text-slate-500 mt-3">
                Tip: Toggle boards as "Inactive" if they're not ready for reservations yet. You can activate them later.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
        >
          ← Back
        </button>
        <button
          onClick={() => setHasIssue(true)}
          className="px-6 py-3 bg-white hover:bg-coral-50 text-coral-600 font-bold rounded-lg border-2 border-coral-500 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
        >
          ⚠️ Something's Wrong
        </button>
        <button
          onClick={() => onComplete({
            boardStatuses,
            editedGymData
          })}
          className="flex-1 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
        >
          ✓ Looks Perfect →
        </button>
      </div>
    </div>
  );
};

export default ConfirmGym;
